import type { TGender } from '~constants/gender';
import { RegisterInbound, RegisterOutbound } from '~features/annals';
import type {
	TShopItem,
	TShopNeuronsPack,
	TShopSubscription,
} from '~services/api/credits/types';
import { transformShopItemToGTMData } from '~utils/analytics';
import { calculateAge } from '~utils/dates';

import {
	REGISTRATION_FORM_MAIN_ID,
	REGISTRATION_FORM_MAIN_NAME,
} from './constants';

class GTM {
	user_id: string | null = null;
	email: string | null = null;
	setUserId = (id: string) => {
		if (this.user_id !== id) {
			this.user_id = id;
		}
	};
	setEmail = (email: string) => {
		this.email = email;
	};
	userAuthorized = () => {
		window.dataLayer?.push({
			event: 'event_authorized_changeDL',
			user_id: this.user_id,
			data: {
				email: this.email,
			},
		});
	};

	userSignedUp = (
		method: string,
		afid?: string,
		birthday?: string,
		name?: string,
		registerOutbound = RegisterOutbound.NoneIdentified,
		formName = REGISTRATION_FORM_MAIN_NAME,
		formId = REGISTRATION_FORM_MAIN_ID
	) => {
		const age = birthday ? calculateAge(birthday) : undefined;
		window.dataLayer?.push({
			event: 'event_form_action_changeDL',
			user_id: this.user_id,
			afid,
			form: {
				name: formName,
				id: formId,
			},
			data: {
				name,
				age,
			},
			method,
			registration_out: registerOutbound,
		});
	};

	registerFormShown = (
		registerInbound = RegisterInbound.NoneIdentified,
		botId: string | undefined
	) => {
		window.dataLayer?.push({
			event: 'registration_shown',
			user_id: this.user_id,
			registration_in: registerInbound,
			character: botId,
		});
	};

	generateLead = (
		afid?: string,
		registerOutbound = RegisterOutbound.NoneIdentified,
		formName = REGISTRATION_FORM_MAIN_NAME,
		formId = REGISTRATION_FORM_MAIN_ID
	) => {
		window.dataLayer?.push({
			event: 'event_registered_changeDL',
			user_id: this.user_id,
			afid,
			form: {
				name: formName,
				id: formId,
			},
			data: {
				email: this.email,
			},
			registration_out: registerOutbound,
		});
	};

	userConfirmedEmail = (
		afid: string,
		email: string,
		birthday?: string,
		gender?: TGender,
		country?: string
	) => {
		const age = birthday ? calculateAge(birthday) : undefined;
		const data = {
			email,
			gender: gender?.toString(),
			age,
		};

		window.dataLayer?.push({
			event: 'confirm_email',
			user_id: this.user_id,
			country,
			afid,
			data,
		});
	};

	userRedirectedFromNotification = (
		afid: string,
		email: string,
		birthday?: string,
		gender?: TGender,
		country?: string
	) => {
		const age = birthday ? calculateAge(birthday) : undefined;
		const data = {
			email,
			gender: gender?.toString(),
			age,
		};

		window.dataLayer?.push({
			event: 'notification',
			user_id: this.user_id,
			country: country ?? 'N/A',
			afid,
			data,
		});
	};

	ageConfirmed = () => {
		window.dataLayer?.push({
			event: 'content_agreed',
			user_id: this.user_id,
		});
	};

	registrationFormFill = (
		field: string,
		formName = REGISTRATION_FORM_MAIN_NAME,
		formId = REGISTRATION_FORM_MAIN_ID
	) => {
		window.dataLayer?.push({
			event: 'event_form_action_changeDL',
			user_id: this.user_id,
			registration_forms: {
				name: 'start-fill',
				form: {
					name: formName,
					id: formId,
					field,
				},
			},
		});
	};

	characterChosen = (botId: string) => {
		window.dataLayer?.push({
			event: 'character_chosen',
			user_id: this.user_id,
			character: botId,
		});
	};

	viewSubscription = (items: TShopSubscription[]) => {
		window.dataLayer?.push({
			event: 'view_item_list',
			user_id: this.user_id,
			ecommerce: {
				items: items.map((item, index) => ({
					...transformShopItemToGTMData(item),
					index: index + 1,
				})),
			},
		});
	};

	userDidReceiveSextingMessage = (botId: string) => {
		window.dataLayer?.push({
			event: 'sexting_message',
			user_id: this.user_id,
			value: botId,
		});
	};
	viewNeurons = (items: TShopNeuronsPack[]) => {
		window.dataLayer?.push({
			event: 'view_item_list',
			user_id: this.user_id,
			ecommerce: {
				items: items.map((item, index) => ({
					...transformShopItemToGTMData(item),
					index: index + 1,
				})),
			},
		});
	};
	selectItem = (item: TShopItem, index: number) => {
		try {
			window.dataLayer?.push({
				event: 'select_item',
				user_id: this.user_id,
				ecommerce: {
					value: item.price,
					items: [
						{
							...transformShopItemToGTMData(item),
							index: index + 1,
							quantity: '1',
						},
					],
				},
			});
		} catch {}
	};
	beginCheckout = (item: TShopItem) => {
		try {
			window.dataLayer?.push({
				event: 'begin_checkout',
				user_id: this.user_id,
				ecommerce: {
					items: [
						{
							...transformShopItemToGTMData(item, true),
							quantity: '1',
						},
					],
				},
			});
		} catch {}
	};
	purchase = (item: TShopItem, tId: string, isFirst = false) => {
		const firstPurchaseData = isFirst ? { item_category2: 'payment-1st' } : {};
		window.dataLayer?.push({
			event: 'purchase',
			user_id: this.user_id,
			ecommerce: {
				value: item.price,
				currency: item.currency.toUpperCase(),
				transaction_id: tId,
				items: [
					{
						...transformShopItemToGTMData(item, true),
						item_variant: item.meta.Subscription ? 'auto' : 'non-recurring',
						...firstPurchaseData,
						quantity: '1',
					},
				],
			},
		});
	};
	createFreeImage = (generated: boolean) => {
		window.dataLayer?.push({
			event: 'create_free_image_in_na',
			generated,
			user_id: this.user_id,
		});
	};
}

export const GTMAnalytics = new GTM();
